import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import {
    Card,
    CardContent,
    CardHeader,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { createDateFromDDMMYYYY } from "@icarius-utils/date";
import CommonPage from "@icarius-common/commonPage";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import ProfileImg from "@icarius-common/profileImg";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import Container from "../container";
import ProgressBar from "../trainingSummary/progressBar";
import Bar from "./bar";
import GridDialog from "./gridDialog";
import { getSpecificTrainingStatsAction } from "@icarius-pages/myTraining/actions";

const FILTER_BY_FINISHED_OPTIONS = { all: "all", finished: "finished", active: "active" };

const Stats = (props) => {

    const {
        isLoading,
        trainingStats,
        participantStats,
        handleGoBack,
    } = props;

    const [individualTrainingId, setIndividualTrainingId] = useState("");
    const [multipleTrainingIds, setMultipleTrainingIds] = useState(() => trainingStats.map((training) => training.id));
    const [filterByFinished, setFilterByFinished] = useState(FILTER_BY_FINISHED_OPTIONS.all);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedParticipant, setSelectedParticipant] = useState(null);

    const [gridData, setGridData] = useState(null);

    const dispatch = useDispatch();

    const filteredTrainingStats = useMemo(() => {
        return trainingStats
            .filter((training) => {
                const formattedDateFrom = dateFrom ? dateFrom.setHours(0, 0, 0, 0) : null;
                const formattedDateTo = dateTo ? dateTo.setHours(0, 0, 0, 0) : null;
                const formattedTrainingDateFrom = createDateFromDDMMYYYY(training.dateFrom).setHours(0, 0, 0, 0);
                const formattedTrainingDateTo = createDateFromDDMMYYYY(training.dateTo).setHours(0, 0, 0, 0);

                if (filterByFinished === FILTER_BY_FINISHED_OPTIONS.finished && !training.isFinished) return false;
                if (filterByFinished === FILTER_BY_FINISHED_OPTIONS.active && training.isFinished) return false;
                if (formattedDateFrom && formattedTrainingDateFrom < formattedDateFrom) return false;
                if (formattedDateTo && formattedTrainingDateTo > formattedDateTo) return false;
                return true;
            })
    }, [trainingStats, filterByFinished, dateFrom, dateTo])

    const getTrainingById = useCallback((trainingId) => trainingStats.find((training) => training.id === Number(trainingId)), [trainingStats]);
    const getTrainingParticipants = useCallback((trainingId) => participantStats.filter((participant) => Object.keys(participant.activitiesFinished).map(Number).includes(trainingId)), [participantStats]);

    const filteredParticipants = useMemo(() => {
        return participantStats.filter((participant) => {
            const participantTrainingIds = Object.keys(participant.activitiesFinished).map(Number);
            return participantTrainingIds.some((trainingId) => multipleTrainingIds.includes(trainingId));
        })
    }, [participantStats, multipleTrainingIds])

    const multipleTrainingStats = useMemo(() => {
        const trainingList = multipleTrainingIds.map((trainingId) => getTrainingById(trainingId));

        const statsByTraining = trainingList.map((training) => {
            const participants = getTrainingParticipants(training.id);
            const timesFinished = participants.filter((participant) => participant.activitiesFinished[training.id] === training.activitiesAmount).length;

            return {
                name: training.name,
                activitiesAmount: training.activitiesAmount,
                timesFinished,
                participantsAmount: participants.length,
                percentageFinished: Number((timesFinished / (participants.length || 1) * 100).toFixed(2)),
            };
        });

        return {
            trainingList,
            trainingNames: statsByTraining.map((item) => item.name),
            activitiesByTraining: statsByTraining.map((item) => item.activitiesAmount),
            timesFinishedByTraining: statsByTraining.map((item) => item.timesFinished),
            participantsByTraining: statsByTraining.map((item) => item.participantsAmount),
            percentageFinishedByTraining: statsByTraining.map((item) => item.percentageFinished),
        };
    }, [getTrainingById, getTrainingParticipants, multipleTrainingIds]);

    const individualTrainingStats = useMemo(() => {
        const participantsNames = getTrainingParticipants(individualTrainingId).map(participant => participant.name);
        const percentageFinished = getTrainingParticipants(individualTrainingId).map((participant) => Number((participant.activitiesFinished[individualTrainingId] / getTrainingById(individualTrainingId).activitiesAmount * 100).toFixed(2)))

        return {
            participantsNames,
            percentageFinished,
        }
    }, [getTrainingById, getTrainingParticipants, individualTrainingId])

    const selectedParticipantStats = useMemo(() => {
        if (!selectedParticipant) {
            return {
                activityNames: [],
                percentageFinishedByTraining: [],
                totalProgress: 0,
                activitiesFinished: [],
            };
        }

        const activityNames = Object.keys(selectedParticipant.activitiesFinished).map((trainingId) => getTrainingById(trainingId).name);
        const percentageFinished = Object.keys(selectedParticipant.activitiesFinished).map((trainingId) => {
            const trainingTotalActivities = getTrainingById(trainingId).activitiesAmount;
            const trainingActivitiesFinished = selectedParticipant.activitiesFinished[trainingId];
            return Number((trainingActivitiesFinished / trainingTotalActivities * 100).toFixed(2));
        });
        const totalProgress = Number((percentageFinished.reduce((a, b) => a + b, 0) / percentageFinished.length).toFixed(2));
        const activitiesFinished = Object.values(selectedParticipant.activitiesFinished).map(Number);

        return {
            activityNames,
            percentageFinished,
            totalProgress,
            activitiesFinished,
        };
    }, [getTrainingById, selectedParticipant]);

    const openSelectTrainingWarning = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("filtersAppliedSelectTrainingAgain"), severity: "warning" }));
    }

    const handleGetSpecificTrainingGridData = (index) => {
        const clickedTraining = multipleTrainingStats.trainingList[index];
        dispatch(getSpecificTrainingStatsAction(clickedTraining.id))
            .then((resp) => {
                if (resp.status === 200) {
                    setGridData({
                        columnDefPage: "activitiesByTraining",
                        trainingName: `${clickedTraining.name} (${getLocalizedString("activities")})`,
                        data: resp.data.data,
                    })
                }
            })
    }

    const handleCreateParticipantsByTrainingGridData = (index) => {
        const clickedTraining = multipleTrainingStats.trainingList[index];
        const trainingParticipants = getTrainingParticipants(clickedTraining.id);

        setGridData({
            columnDefPage: "participantsByTraining",
            trainingName: clickedTraining.name,
            data: trainingParticipants.map((participant) => {
                return ({
                    code: participant.code,
                    name: participant.name,
                    activitiesFinished: participant.activitiesFinished[clickedTraining.id],
                    progress: participant.activitiesFinished[clickedTraining.id] / clickedTraining.activitiesAmount,
                })
            })
        })
    }

    const handleCloseGridDialog = () => {
        setGridData(null);
    }

    const goBackButton = () => (
        <CustomIconButton
            title={getLocalizedString("goBack")}
            onClick={handleGoBack}
            type={"goBack"}
        />
    )

    return (
        <CommonPage
            title={`${getLocalizedString("lms")} (${getLocalizedString("stats")})`}
            menuItems={[goBackButton]}
            isLoading={isLoading}
            isNotGridPage
        >
            <Grid container item spacing={2} xs={12} justify="center" style={{ marginBottom: 20 }}>
                <Grid container item alignItems="center" xs={12} spacing={2} style={{ paddingBottom: 15 }}>
                    <Grid container item alignItems="center" xs={12} sm={6}>
                        <FormControl style={{ width: "100%" }}>
                            <InputLabel id={"participant-type"}>
                                {getLocalizedString("type")}
                            </InputLabel>
                            <Select
                                value={filterByFinished}
                                labelId={"participant-type"}
                                id={"type"}
                                onChange={(e) => {
                                    setFilterByFinished(e.target.value);
                                    setMultipleTrainingIds([]);
                                    setIndividualTrainingId("");
                                    setSelectedParticipant("");
                                    openSelectTrainingWarning();
                                }}
                                margin={"none"}
                            >
                                {
                                    Object.keys(FILTER_BY_FINISHED_OPTIONS).map((item) => (
                                        <MenuItem
                                            className={"whiteText"}
                                            key={item}
                                            value={item}
                                        >
                                            {getLocalizedString(item)}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={6} md={3}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                            <DatePicker
                                label={getLocalizedString("dateFrom")}
                                cancelLabel={getLocalizedString("cancel")}
                                okLabel={getLocalizedString("ok")}
                                clearLabel={getLocalizedString("clear")}
                                invalidDateMessage=""
                                format="dd/MM/yyyy"
                                margin="none"
                                value={dateFrom}
                                onChange={(e) => {
                                    setDateFrom(e);
                                    setDateTo(null);
                                    setMultipleTrainingIds([]);
                                    setIndividualTrainingId("");
                                    setSelectedParticipant("");
                                    openSelectTrainingWarning();
                                }}
                                fullWidth
                                clearable
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={6} md={3}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                            <DatePicker
                                label={getLocalizedString("dateTo")}
                                cancelLabel={getLocalizedString("cancel")}
                                okLabel={getLocalizedString("ok")}
                                clearLabel={getLocalizedString("clear")}
                                invalidDateMessage=""
                                format="dd/MM/yyyy"
                                margin="none"
                                value={dateTo}
                                onChange={(e) => {
                                    setDateTo(e);
                                    setMultipleTrainingIds([]);
                                    setIndividualTrainingId("");
                                    setSelectedParticipant("");
                                    openSelectTrainingWarning();
                                }}
                                minDate={dateFrom}
                                fullWidth
                                clearable
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid container item alignItems="center" xs={12}>
                        {
                            !Boolean(filteredTrainingStats.length) &&
                            <Alert variant="filled" severity="info" style={{ width: "100%" }}>
                                {getLocalizedString("noTrainingMatchFilter")}
                            </Alert>
                        }
                        {
                            Boolean(filteredTrainingStats.length) &&
                            <FormControl style={{ width: "100%" }}>
                                <InputLabel id={"participant-trainingPlural"}>
                                    {getLocalizedString("trainingPlural")}
                                </InputLabel>
                                <Select
                                    multiple
                                    value={multipleTrainingIds}
                                    labelId={"participant-trainingPlural"}
                                    id={"trainingPlural"}
                                    onChange={(e) => setMultipleTrainingIds(e.target.value)}
                                    margin={"none"}
                                >
                                    {
                                        filteredTrainingStats.map(item => (
                                            <MenuItem
                                                className={"whiteText"}
                                                key={item.id}
                                                value={item.id}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        }
                    </Grid>
                </Grid>
                {
                    Boolean(multipleTrainingIds?.length) &&
                    <>
                        <Grid container item xs={12}>
                            <Card style={{ width: "100%" }}>
                                <CardHeader title={getLocalizedString("statsByTraining")} />
                                <CardContent style={{ paddingTop: 0 }}>
                                    <Grid container spacing={2}>
                                        <Grid container item xs={12} style={{ padding: 10 }}>
                                            <Alert variant="filled" severity="info" style={{ width: "100%" }}>
                                                {getLocalizedString("clickColumnToViewMoreAboutTraining")}
                                            </Alert>
                                        </Grid>
                                        <Grid container item xs={12}>
                                            <Container title={getLocalizedString("activitiesByTraining")} style={{ width: "100%" }}>
                                                <Bar
                                                    categories={multipleTrainingStats.trainingNames}
                                                    data={multipleTrainingStats.activitiesByTraining}
                                                    handleColumnClick={handleGetSpecificTrainingGridData}
                                                    getTooltipText={() => `${getLocalizedString("activities").toLowerCase()}`}
                                                />
                                            </Container>
                                        </Grid>
                                        <Grid container item xs={12}>
                                            <Container title={getLocalizedString("usersByTraining")} style={{ width: "100%" }}>
                                                <Bar
                                                    categories={multipleTrainingStats.trainingNames}
                                                    data={multipleTrainingStats.participantsByTraining}
                                                    handleColumnClick={handleCreateParticipantsByTrainingGridData}
                                                    getTooltipText={() => `${getLocalizedString("participants").toLowerCase()}`}
                                                />
                                            </Container>
                                        </Grid>
                                        <Grid container item xs={12}>
                                            <Container title={getLocalizedString("percentageUsersFinishedByTraining")} style={{ width: "100%" }}>
                                                <Bar
                                                    categories={multipleTrainingStats.trainingNames}
                                                    data={multipleTrainingStats.percentageFinishedByTraining}
                                                    handleColumnClick={handleCreateParticipantsByTrainingGridData}
                                                    getTooltipText={(index) => `(${multipleTrainingStats.timesFinishedByTraining[index]}/${getTrainingParticipants(multipleTrainingIds[index]).length} ${getLocalizedString("activities").toLowerCase()})`}
                                                    isPercentage
                                                />
                                            </Container>
                                        </Grid>
                                        {
                                            Boolean(filteredTrainingStats.length) &&
                                            <Grid container item xs={12}>
                                                <Container title={getLocalizedString("percentageFinishedByUser")} style={{ width: "100%" }}>
                                                    <Grid container item alignItems="center" xs={12}>
                                                        <FormControl style={{ width: "100%", marginTop: 10 }}>
                                                            <InputLabel id={"participant-trainingSingular"}>
                                                                {getLocalizedString("trainingSingular")}
                                                            </InputLabel>
                                                            <Select
                                                                value={individualTrainingId}
                                                                labelId={"participant-trainingSingular"}
                                                                id={"trainingSingular"}
                                                                onChange={(e) => setIndividualTrainingId(e.target.value)}
                                                                margin={"none"}
                                                            >
                                                                {
                                                                    filteredTrainingStats.map(item => (
                                                                        <MenuItem
                                                                            className={"whiteText"}
                                                                            key={item.id}
                                                                            value={item.id}
                                                                        >
                                                                            {item.name}
                                                                        </MenuItem>
                                                                    ))
                                                                }
                                                            </Select>
                                                        </FormControl>
                                                        {
                                                            !Boolean(individualTrainingId) &&
                                                            <Alert variant="filled" severity="warning" style={{ width: "100%", marginTop: 10 }}>
                                                                {getLocalizedString("youMustSelectTraining")}
                                                            </Alert>
                                                        }
                                                    </Grid>
                                                    <Bar
                                                        categories={individualTrainingStats.participantsNames}
                                                        data={individualTrainingStats.percentageFinished}
                                                        getTooltipText={(index) => `(${getTrainingParticipants(individualTrainingId)[index].activitiesFinished[individualTrainingId]}/${getTrainingById(individualTrainingId).activitiesAmount} ${getLocalizedString("activities").toLowerCase()})`}
                                                        isPercentage
                                                    />
                                                </Container>
                                            </Grid>
                                        }
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid container item xs={12}>
                            <Card style={{ width: "100%" }}>
                                <CardHeader title={getLocalizedString("statsByParticipant")} />
                                <CardContent>
                                    <Grid container direction="column" spacing={2}>
                                        <Grid container item alignItems="center" xs={12}>
                                            <Autocomplete
                                                options={filteredParticipants}
                                                value={selectedParticipant}
                                                onChange={(_, newParticipant) => setSelectedParticipant(newParticipant)}
                                                getOptionSelected={(selected) => Boolean(filteredParticipants.find((participant) => participant.code === selected.code))}
                                                noOptionsText={''}
                                                getOptionLabel={(participant) => participant?.name || ""}
                                                style={{ width: "100%" }}
                                                renderInput={(params) => <TextField {...params} label={getLocalizedString("participant")} />}
                                                clearOnBlur
                                            />
                                        </Grid>
                                        {
                                            Boolean(selectedParticipant) &&
                                            <>
                                                <Grid container item xs={12}>
                                                    <div
                                                        style={{
                                                            marginTop: 10,
                                                            marginBottom: 10,
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 10,
                                                        }}
                                                    >
                                                        <ProfileImg
                                                            image={selectedParticipant.photo}
                                                            size={50}
                                                        />
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                                                            <Typography className="whiteText" style={{ fontSize: 18 }}>
                                                                {selectedParticipant.name}
                                                            </Typography>
                                                            <Tooltip title={getLocalizedString("totalFinishedPercentage")} arrow>
                                                                <div style={{ maxWidth: 400 }}>
                                                                    <ProgressBar
                                                                        value={selectedParticipantStats.totalProgress}
                                                                        small
                                                                    />
                                                                </div>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </Grid>
                                                <Grid container item xs={12}>
                                                    <Container title={getLocalizedString("percentageFinishedByTraining")} style={{ width: "100%" }}>
                                                        <Bar
                                                            categories={selectedParticipantStats.activityNames}
                                                            data={selectedParticipantStats.percentageFinished}
                                                            getTooltipText={(index) => {
                                                                const training = getTrainingById(Number(Object.keys(selectedParticipant.activitiesFinished)[index]));
                                                                return `(${Object.values(selectedParticipant.activitiesFinished)[index]}/${training.activitiesAmount} ${getLocalizedString("activities").toLowerCase()})`;
                                                            }}
                                                            isPercentage
                                                        />
                                                    </Container>
                                                </Grid>
                                                <Grid container item xs={12}>
                                                    <Container title={getLocalizedString("activitiesFinishedByTraining")} style={{ width: "100%" }}>
                                                        <Bar
                                                            categories={selectedParticipantStats.activityNames}
                                                            data={selectedParticipantStats.activitiesFinished}
                                                            getTooltipText={() => `${getLocalizedString("activities").toLowerCase()}`}
                                                        />
                                                    </Container>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                }
            </Grid>
            {
                Boolean(gridData) &&
                <GridDialog
                    open={Boolean(gridData)}
                    data={gridData}
                    handleClose={handleCloseGridDialog}
                />
            }
        </CommonPage>
    )
}

export default Stats;